import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {
  Email,
  IdentificationStatus,
  Money,
  PersonalData,
  Phone,
  Profile,
  Role,
  UserStatus
} from "api-client";
import {Nullable} from "../utils";

export type ProfileState = {
    uuid?: string;
    role: Role;
    status: UserStatus;
    username: string;
    name: string;
    balance: Money;
    reservedFunds: Money;
    paymentCode?: string;
    bankAccounts: Array<string>,
    selectedBankAccount: string,
    email: Nullable<Email>;
    phone: Phone;
    phoneVerified: boolean;
    isVerified: boolean;
    personalData: Nullable<PersonalData>;
    lenderCanDeposits: boolean;
    identificationStatus: IdentificationStatus;
    hasIdentificationDraft: boolean;
    identificationDate: string;
    isEntrepreneur: boolean;
    isLegalEntity: boolean;
    agentCode: Nullable<string>;
    hasTransactions: boolean;
    newNotificationCount: number;
    isQualified: Nullable<boolean>;
    borrowerOnboardingComplete: boolean | null;
    referralEnabled: Nullable<boolean>;
}

const initialState: ProfileState = {
  uuid: undefined,
  role: Role.LENDER,
  status: UserStatus.UNKNOWN,
  username: "",
  name: "",
  balance: new Money(0, ""),
  reservedFunds: new Money(0, ""),
  paymentCode: "",
  //TODO: ADD BANK ACCOUNTS TO API
  bankAccounts: ["48568455593262719", "48568455593262718", "48568455593262717"],
  selectedBankAccount: "48568455593262719",
  email: null,
  phone: new Phone("", ""),
  phoneVerified: false,
  isVerified: false,
  personalData: null,
  lenderCanDeposits: false,
  identificationStatus: IdentificationStatus.NOT_VERIFIED,
  hasIdentificationDraft: false,
  identificationDate: '',
  isEntrepreneur: false,
  isLegalEntity: false,
  agentCode: null,
  hasTransactions: false,
  newNotificationCount: 0,
  isQualified: null,
  borrowerOnboardingComplete: null,
  referralEnabled: null,
};

//TODO: refactor role-dependent fields
export const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    set: (state: ProfileState, {payload}: PayloadAction<Profile>) => {
      state.uuid = payload.uuid;
      state.role = payload.role;
      state.status = payload.status;
      state.username = payload.username;
      state.name = payload.name ?? null;
      state.balance = payload.balance;
      state.reservedFunds = payload.reservedFunds;
      state.paymentCode = payload.paymentCode;
      state.email = payload.email;
      state.phone = payload.phone;
      state.phoneVerified = payload.phoneVerified;
      state.isVerified = payload.isVerified || false;
      state.personalData = payload.personalData || null;
      state.isEntrepreneur = state.status === UserStatus.ENTREPRENEUR;
      state.isLegalEntity = state.status === UserStatus.LEGAL_ENTITY;
      state.hasIdentificationDraft = payload.hasIdentificationDraft || false;
      state.identificationStatus = payload.identificationStatus || IdentificationStatus.NOT_VERIFIED ;
      state.agentCode = payload.lender?.agentCode || null;
      state.hasTransactions = payload.lender?.hasTransactions || false;
      state.newNotificationCount = payload.newNotificationCount;
      state.isQualified = payload.lender?.isQualified || null;
      state.identificationDate = payload?.identificationDate?.toString() || '';
      state.referralEnabled = payload.lender?.referralEnabled ?? null;
    },
    setBorrowerOnboardingComplete: (state: ProfileState, {payload}: PayloadAction<boolean | null>) => {
      state.borrowerOnboardingComplete = payload;
    },
    setReferralEnabled: (state: ProfileState, {payload}: PayloadAction<boolean>) => {
      state.referralEnabled = payload;
    },
    subtractBalance: (state: ProfileState, {payload}: PayloadAction<number>) => {
      if (state.balance) {
        state.balance = new Money(state.balance.amount - payload, state.balance.currencyCode);
      }
    },
    setIdentificationDraft: (state: ProfileState, {payload}: PayloadAction<boolean>) => {
      state.hasIdentificationDraft = payload;
    },
    addAgentCode: (state: ProfileState, {payload}: PayloadAction<string>) => {
      state.agentCode = payload;
    },
    changeNotificationCount: (state: ProfileState, {payload}: PayloadAction<number>) => {
      state.newNotificationCount = payload;
    },
    selectBankAccount: (state: ProfileState, {payload}: PayloadAction<string>) => {
      state.selectedBankAccount = payload;
    },
    clear: (state: ProfileState) => {
      delete state.uuid;
      delete state.paymentCode;
    }
  }
});

export const {
  set,
  setIdentificationDraft,
  addAgentCode,
  subtractBalance,
  selectBankAccount,
  changeNotificationCount,
  clear,
  setBorrowerOnboardingComplete,
  setReferralEnabled,
} = profileSlice.actions;

export default profileSlice.reducer;

